import { useState, useEffect, useCallback, useMemo } from 'react'

/**
 * Generate a unique window ID for this renderer instance
 */
function generateWindowId(): string {
  // Try to get window ID from URL hash or generate new one
  const hash = window.location.hash
  const match = hash.match(/windowId=([^&]+)/)
  if (match) {
    return match[1]
  }
  return `window-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

/**
 * Hook for managing window-specific UI state
 *
 * Window-specific states (like sidebar open/closed) stay local to each window.
 * This is different from global state which is shared across all windows.
 *
 * @example
 * ```tsx
 * const { uiState, updateUiState } = useWindowState()
 * const isSidebarOpen = uiState.sidebarLeft
 * ```
 */
export function useWindowState() {
  const windowId = useMemo(() => generateWindowId(), [])

  const [uiState, setUiState] = useState(() => {
    // Read from localStorage synchronously to avoid flash/animation on load
    try {
      const cached = localStorage.getItem('sidebar-state')
      if (cached) {
        const parsed = JSON.parse(cached)
        return {
          sidebarLeft: parsed.sidebarLeft ?? true,
          sidebarRight: parsed.sidebarRight ?? true,
          viewMode: 'full' as 'full' | 'compact' | 'zen'
        }
      }
    } catch { /* ignore */ }
    return {
      sidebarLeft: true,
      sidebarRight: true,
      viewMode: 'full' as 'full' | 'compact' | 'zen'
    }
  })

  useEffect(() => {
    if (!window.api) return

    // Get initial window state from Main Process
    const loadWindowState = async () => {
      try {
        // Load persisted sidebar state from settings
        const savedSidebar = await window.api.invoke('get-sidebar-state')

        const state = await window.api.invoke('window:get-state', windowId)
        if (state?.ui) {
          setUiState(state.ui)
        } else if (savedSidebar) {
          setUiState(prev => ({
            ...prev,
            sidebarLeft: savedSidebar.sidebarLeft ?? true,
            sidebarRight: savedSidebar.sidebarRight ?? true
          }))
        }
      } catch (error) {
        console.error('[useWindowState] Failed to load window state:', error)
      }
    }

    loadWindowState()

    // Listen for window state updates
    const handleWindowStateUpdate = (data: { windowId: string; state: any }) => {
      if (data.windowId === windowId && data.state?.ui) {
        setUiState(data.state.ui)
      }
    }

    window.api.receive('window-state-updated', handleWindowStateUpdate)

    // Cleanup
    return () => {
      window.api?.removeAllListeners('window-state-updated')
    }
  }, [windowId])

  /**
   * Update window-specific UI state
   */
  const updateUiState = useCallback((updates: Partial<typeof uiState>) => {
    const newState = { ...uiState, ...updates }
    setUiState(newState)

    // Notify Main Process (for info only, not for sync)
    window.api?.send('window:ui-state', windowId, newState)

    // Persist sidebar state to settings + localStorage cache
    if ('sidebarLeft' in updates || 'sidebarRight' in updates) {
      const sidebarState = {
        sidebarLeft: newState.sidebarLeft,
        sidebarRight: newState.sidebarRight
      }
      localStorage.setItem('sidebar-state', JSON.stringify(sidebarState))
      window.api?.invoke('save-sidebar-state', sidebarState)
    }
  }, [windowId, uiState])

  /**
   * Toggle sidebar visibility
   */
  const toggleSidebar = useCallback((side: 'left' | 'right') => {
    updateUiState({
      [`sidebar${side.charAt(0).toUpperCase() + side.slice(1)}`]: !uiState[`sidebar${side.charAt(0).toUpperCase() + side.slice(1)}`]
    })
  }, [uiState, updateUiState])

  return {
    windowId,
    uiState,
    updateUiState,
    toggleSidebar
  }
}

/**
 * Hook for view mode management
 */
export function useViewMode() {
  const { uiState, updateUiState } = useWindowState()
  const [isTransitioning, setIsTransitioning] = useState(false)

  useEffect(() => {
    if (!window.api) return

    // Listen for view mode changes from Main Process
    const handleViewModeChange = (mode: 'full' | 'compact' | 'zen') => {
      updateUiState({ viewMode: mode })
      setIsTransitioning(false)
    }

    window.api.receive('view-mode-changed', handleViewModeChange)

    return () => {
      window.api?.removeAllListeners('view-mode-changed')
    }
  }, [updateUiState])

  const switchToCompactMode = useCallback(async () => {
    if (!window.api) return
    setIsTransitioning(true)
    await window.api.invoke('switch-view-mode', 'compact')
  }, [])

  const switchToZenMode = useCallback(async () => {
    if (!window.api) return
    setIsTransitioning(true)
    await window.api.invoke('switch-view-mode', 'zen')
  }, [])

  const switchToFullMode = useCallback(async () => {
    if (!window.api) return
    setIsTransitioning(true)
    await window.api.invoke('switch-view-mode', 'full')
  }, [])

  return {
    viewMode: uiState.viewMode,
    isFullMode: uiState.viewMode === 'full',
    isCompactMode: uiState.viewMode === 'compact',
    isZenMode: uiState.viewMode === 'zen',
    isTransitioning,
    switchToCompactMode,
    switchToZenMode,
    switchToFullMode
  }
}